import React, { useEffect, useState } from "react";
import { HiOutlineDocumentText, HiOutlineEye, HiOutlineCheck, HiOutlineX, HiOutlineClock, HiOutlineCalendar, HiOutlineCurrencyDollar, HiOutlineUserGroup, HiOutlineClipboardList, HiOutlineFilter, HiOutlineSearch, HiOutlineCheckCircle, HiOutlineXCircle, HiOutlineArrowRight, HiOutlinePlus, HiOutlineDocumentDownload, HiOutlinePrinter } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';

export default function ProformaPage() {
  const navigate = useNavigate();
  const [proformas, setProformas] = useState([]);
  const [partners, setPartners] = useState([]);
  const [showDetail, setShowDetail] = useState(false);
  const [detailProforma, setDetailProforma] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [partnerFilter, setPartnerFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");

  // Proformaları ve partnerleri yükle
  useEffect(() => {
    fetch('/api/proformas')
      .then(res => res.json())
      .then(data => setProformas(data))
      .catch(() => setProformas([]));

    fetch('/api/partners')
      .then(res => res.json())
      .then(data => setPartners(data))
      .catch(() => setPartners([]));
  }, []);

  // Proforma durumunu güncelle
  const handleStatusUpdate = async (proformaId, newStatus) => {
    try {
      const resp = await fetch(`/api/proformas/${proformaId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (!resp.ok) throw new Error('Durum güncellenemedi');
      
      // Listeyi güncelle
      fetch('/api/proformas')
        .then(res => res.json())
        .then(data => setProformas(data));
    } catch (err) {
      alert('Durum güncellenemedi: ' + err.message);
    }
  };

  // Detay modalı aç
  const handleShowDetail = async (proforma) => {
    try {
      const resp = await fetch(`/api/proformas/${proforma.id}`);
      const data = await resp.json();
      setDetailProforma(data);
      setShowDetail(true);
    } catch (err) {
      alert('Proforma detayı yüklenemedi: ' + err.message);
    }
  };

  // PDF oluştur
  const handleGeneratePDF = async (proforma) => {
    try {
      const resp = await fetch(`/api/proformas/${proforma.id}/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (resp.ok) {
        const blob = await resp.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${proforma.proforma_no}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('PDF oluşturulamadı');
      }
    } catch (err) {
      alert('PDF oluşturma hatası: ' + err.message);
    }
  };

  // PDF önizleme
  const handlePdfPreview = () => {
    setShowPdfPreview(true);
  };

  // Print fonksiyonu
  const handlePrint = () => {
    const iframe = document.querySelector('iframe[src*="/api/proformas"]');
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.print();
    } else {
      // Fallback: Yeni pencerede print
      const printWindow = window.open(`/api/proformas/${detailProforma.id}/preview`, '_blank');
      printWindow.onload = function() {
        printWindow.print();
      };
    }
  };

  // Filtreleme
  const filteredProformas = proformas.filter(proforma => {
    const matchesSearch = proforma.proforma_no.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         proforma.partner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         proforma.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || proforma.status === statusFilter;
    const matchesPartner = partnerFilter === "all" || proforma.partner_id.toString() === partnerFilter;
    
    let matchesDate = true;
    if (dateFilter !== "all") {
      const today = new Date();
      const proformaDate = new Date(proforma.request_date);
      const diffTime = Math.abs(today - proformaDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      switch (dateFilter) {
        case "today":
          matchesDate = diffDays === 0;
          break;
        case "week":
          matchesDate = diffDays <= 7;
          break;
        case "month":
          matchesDate = diffDays <= 30;
          break;
      }
    }
    
    return matchesSearch && matchesStatus && matchesPartner && matchesDate;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'converted_to_sale': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Bekliyor';
      case 'approved': return 'Onaylandı';
      case 'rejected': return 'Reddedildi';
      case 'converted_to_sale': return 'Satışa Dönüştürüldü';
      default: return 'Bilinmiyor';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <HiOutlineClock className="w-4 h-4" />;
      case 'approved': return <HiOutlineCheckCircle className="w-4 h-4" />;
      case 'rejected': return <HiOutlineXCircle className="w-4 h-4" />;
      case 'converted_to_sale': return <HiOutlineArrowRight className="w-4 h-4" />;
      default: return <HiOutlineClock className="w-4 h-4" />;
    }
  };

  return (
    <div className="w-full p-6">
      <h1 className="text-3xl font-bold text-blue-900 mb-8 flex items-center gap-3 px-6 pt-6">
        <HiOutlineDocumentText className="w-8 h-8 text-blue-700" /> Proforma Yönetimi
      </h1>
      <div className="bg-white rounded-2xl shadow-xl p-8 flex flex-col gap-6 border border-blue-100 mt-0">
        <div className="flex justify-between items-center mb-4">
          <span className="font-semibold text-blue-800 text-lg">Proforma Talepleri</span>
          <div className="flex gap-2 items-center">
            <span className="text-sm text-gray-600">
              Toplam: {filteredProformas.length} proforma
            </span>
            <button
              onClick={() => navigate('/proforma-olustur')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              <HiOutlinePlus className="w-4 h-4" />
              Yeni Proforma Oluştur
            </button>
          </div>
        </div>

        {/* Gelişmiş Filtreler */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-blue-900 mb-1">Proforma Ara</label>
            <input
              type="text"
              placeholder="Proforma no, partner adı..."
              className="px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-200 text-blue-900"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-blue-900 mb-1">Durum Filtresi</label>
            <select
              className="px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-200 text-blue-900"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Tüm Durumlar</option>
              <option value="pending">Bekliyor</option>
              <option value="approved">Onaylandı</option>
              <option value="rejected">Reddedildi</option>
              <option value="converted_to_sale">Satışa Dönüştürüldü</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-blue-900 mb-1">Partner Filtresi</label>
            <select
              className="px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-200 text-blue-900"
              value={partnerFilter}
              onChange={(e) => setPartnerFilter(e.target.value)}
            >
              <option value="all">Tüm Partnerler</option>
              {partners.map(partner => (
                <option key={partner.id} value={partner.id}>{partner.name}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-blue-900 mb-1">Tarih Filtresi</label>
            <select
              className="px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-200 text-blue-900"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <option value="all">Tüm Tarihler</option>
              <option value="today">Bugün</option>
              <option value="week">Son 7 Gün</option>
              <option value="month">Son 30 Gün</option>
            </select>
          </div>
        </div>

        {/* Proforma Listesi */}
        <div className="overflow-x-auto rounded-xl border border-blue-100">
          <table className="min-w-full text-sm">
            <thead className="bg-blue-50">
              <tr className="text-blue-900">
                <th className="px-3 py-2 text-left">Proforma No</th>
                <th className="px-3 py-2 text-left">Partner</th>
                <th className="px-3 py-2 text-left">Talep Tarihi</th>
                <th className="px-3 py-2 text-left">Teslimat Tarihi</th>
                <th className="px-3 py-2 text-left">Tutar</th>
                <th className="px-3 py-2 text-left">Durum</th>
                <th className="px-3 py-2 text-left">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredProformas.length === 0 && (
                <tr><td colSpan={7} className="text-center text-gray-400 py-6">Hiç proforma bulunamadı.</td></tr>
              )}
              {filteredProformas.map(proforma => (
                <tr key={proforma.id} className="hover:bg-blue-50 transition">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-sm font-bold">
                        <HiOutlineDocumentText className="w-4 h-4" />
                      </span>
                      <span className="font-mono font-semibold">{proforma.proforma_no}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-700 text-xs font-bold">
                        <HiOutlineUserGroup className="w-3 h-3" />
                      </span>
                      <div>
                        <div className="font-semibold">{proforma.partner_name}</div>
                        <div className="text-xs text-gray-500">{proforma.partner_code}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <HiOutlineCalendar className="w-4 h-4 text-blue-400" />
                      {new Date(proforma.request_date).toLocaleDateString('tr-TR')}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {proforma.delivery_date ? (
                      <div className="flex items-center gap-1">
                        <HiOutlineCalendar className="w-4 h-4 text-green-400" />
                        {new Date(proforma.delivery_date).toLocaleDateString('tr-TR')}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <HiOutlineCurrencyDollar className="w-4 h-4 text-green-400" />
                      <span className="font-semibold">{proforma.total_amount?.toLocaleString('tr-TR')} {proforma.currency}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 w-fit ${getStatusColor(proforma.status)}`}>
                      {getStatusIcon(proforma.status)}
                      {getStatusText(proforma.status)}
                    </span>
                  </td>
                  <td className="px-3 py-2 flex gap-2">
                    <button 
                      className="px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 text-xs font-semibold flex items-center gap-1" 
                      onClick={() => handleShowDetail(proforma)}
                    >
                      <HiOutlineEye className="w-4 h-4" /> Detay
                    </button>
                    <button 
                      className="px-2 py-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 text-xs font-semibold flex items-center gap-1" 
                      onClick={() => handleGeneratePDF(proforma)}
                    >
                      <HiOutlineDocumentDownload className="w-4 h-4" /> PDF
                    </button>
                    {proforma.status === 'pending' && (
                      <>
                        <button 
                          className="px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 text-xs font-semibold flex items-center gap-1" 
                          onClick={() => handleStatusUpdate(proforma.id, 'approved')}
                        >
                          <HiOutlineCheck className="w-4 h-4" /> Onayla
                        </button>
                        <button 
                          className="px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 text-xs font-semibold flex items-center gap-1" 
                          onClick={() => handleStatusUpdate(proforma.id, 'rejected')}
                        >
                          <HiOutlineX className="w-4 h-4" /> Reddet
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detay Modalı */}
      {showDetail && detailProforma && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-4xl flex flex-col gap-4 border border-blue-100 animate-fade-in max-h-[90vh] overflow-y-auto">
            <div className="text-2xl font-bold text-blue-900 text-center mb-2 flex items-center gap-2">
              <HiOutlineDocumentText className="w-7 h-7 text-blue-700" /> Proforma Detayı
            </div>
            
            {/* Proforma Bilgileri */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div><b>Proforma No:</b> {detailProforma.proforma_no}</div>
              <div><b>Partner:</b> {detailProforma.partner_name} ({detailProforma.partner_code})</div>
              <div><b>Talep Tarihi:</b> {new Date(detailProforma.request_date).toLocaleDateString('tr-TR')}</div>
              <div><b>Teslimat Tarihi:</b> {detailProforma.delivery_date ? new Date(detailProforma.delivery_date).toLocaleDateString('tr-TR') : '-'}</div>
              <div><b>Toplam Tutar:</b> {detailProforma.total_amount?.toLocaleString('tr-TR')} {detailProforma.currency}</div>
              <div><b>Durum:</b> <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(detailProforma.status)}`}>{getStatusText(detailProforma.status)}</span></div>
              <div><b>Notlar:</b> {detailProforma.notes || '-'}</div>
              <div><b>Oluşturulma Tarihi:</b> {new Date(detailProforma.created_at).toLocaleDateString('tr-TR')}</div>
            </div>

            {/* Ürün Listesi */}
            <div className="mt-4">
              <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <HiOutlineClipboardList className="w-5 h-5" /> Proforma Ürünleri
              </h3>
              <div className="overflow-x-auto rounded-xl border border-blue-100">
                <table className="min-w-full text-sm">
                  <thead className="bg-blue-50">
                    <tr className="text-blue-900">
                      <th className="px-3 py-2 text-left">Ürün</th>
                      <th className="px-3 py-2 text-left">Kod</th>
                      <th className="px-3 py-2 text-left">Miktar</th>
                      <th className="px-3 py-2 text-left">Promosyon</th>
                      <th className="px-3 py-2 text-left">Birim Fiyat</th>
                      <th className="px-3 py-2 text-left">Toplam</th>
                      <th className="px-3 py-2 text-left">Notlar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailProforma.items?.map(item => (
                      <tr key={item.id} className="hover:bg-blue-50 transition">
                        <td className="px-3 py-2 font-semibold">{item.product_name}</td>
                        <td className="px-3 py-2 font-mono">{item.product_code}</td>
                        <td className="px-3 py-2">{item.quantity}</td>
                        <td className="px-3 py-2">
                          {item.promotion_quantity ? (
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                              {item.promotion_quantity} adet
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2">{item.unit_price?.toLocaleString('tr-TR')} ₺</td>
                        <td className="px-3 py-2 font-semibold">{item.total_price?.toLocaleString('tr-TR')} ₺</td>
                        <td className="px-3 py-2">{item.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-4">
              <button 
                className="px-4 py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 transition flex items-center gap-2" 
                onClick={handlePdfPreview}
              >
                <HiOutlineEye className="w-4 h-4" />
                PDF Önizleme
              </button>
              <button 
                className="px-4 py-2 rounded bg-purple-600 text-white font-semibold hover:bg-purple-700 transition flex items-center gap-2" 
                onClick={() => handleGeneratePDF(detailProforma)}
              >
                <HiOutlineDocumentDownload className="w-4 h-4" />
                PDF İndir
              </button>
              <button className="px-4 py-2 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700 transition" onClick={() => setShowDetail(false)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Önizleme Modalı */}
      {showPdfPreview && detailProforma && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <button
            className="absolute top-8 right-8 px-4 py-2 rounded-full bg-gray-200 text-gray-700 hover:bg-gray-300 font-bold text-2xl z-50 shadow-lg"
            onClick={() => setShowPdfPreview(false)}
            aria-label="Kapat"
          >
            ×
          </button>
          
          {/* PDF İndir Butonu */}
          <button
            className="absolute top-8 left-8 px-4 py-2 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 transition flex items-center gap-2 z-50 shadow-lg"
            onClick={() => handleGeneratePDF(detailProforma)}
          >
            <HiOutlineDocumentDownload className="w-4 h-4" />
            PDF İndir
          </button>
          
          {/* Print Butonu */}
          <button
            className="absolute top-8 left-40 px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition flex items-center gap-2 z-50 shadow-lg"
            onClick={handlePrint}
          >
            <HiOutlinePrinter className="w-4 h-4" />
            Yazdır
          </button>
          
          <div className="bg-white dark:bg-gray-900 w-full max-w-6xl max-h-[90vh] flex flex-col border border-blue-100 overflow-hidden relative shadow-2xl rounded-2xl">
            <div className="flex-1 w-full h-full flex items-center justify-center overflow-auto p-0">
              <iframe
                src={`/api/proformas/${detailProforma.id}/preview`}
                title="Proforma HTML Önizleme"
                className="w-full h-full border-0"
                style={{ minHeight: '600px' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 